import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { api, type MeView, type Role, type UserView } from './api.ts';
import './users.css';

/**
 * The people who can sign in to this deployment.
 *
 * A panel, not a page: it renders inside Settings, which owns the h1. It is
 * offered only to somebody holding `users.manage`, and the server refuses every
 * route on it regardless of what this console chooses to show.
 *
 * **This screen enforces no rule about who may be changed.** The one that
 * matters — a deployment must keep at least one active owner, or nobody can
 * ever manage people again — depends on a count only the server has. So the
 * console asks, and renders the 409 it gets back. The two self-destructive
 * cases it *can* see from here, suspending yourself and changing your own role,
 * are disabled with the reason on the control, because offering a button whose
 * only outcome is an error is not a courtesy.
 *
 * **Nothing about a password is validated here either.** The minimum length
 * lives on the server; this posts what was typed and renders the sentence that
 * comes back, which states the length. A constant repeated here would be a
 * second copy free to drift out of step with the one that decides.
 */

const ROLES: readonly Role[] = ['owner', 'engineer', 'viewer'];

const ROLE_TEXT: Record<Role, string> = {
  owner: 'Owner',
  engineer: 'Engineer',
  viewer: 'Viewer',
};

const WHEN = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

/**
 * A timestamp as a person reads it. Exported because the account surface shows
 * the same kinds of timestamp and the two must not disagree about how a date
 * looks. An unparseable value renders as itself rather than as "Invalid Date":
 * the console does not know what the server will send next.
 */
export const formatWhen = (iso: string | null): string => {
  if (iso === null) return 'Never';
  const at = new Date(iso);
  return Number.isNaN(at.getTime()) ? iso : WHEN.format(at);
};

const message = (cause: unknown): string =>
  cause instanceof Error ? cause.message : String(cause);

/** What a row's manage drawer is currently offering. */
type PendingKind = 'role' | 'suspend' | 'reinstate' | 'password' | 'sessions';

interface Pending {
  readonly userId: string;
  readonly kind: PendingKind;
}

interface UsersProps {
  readonly me: MeView;
}

export const Users = ({ me }: UsersProps) => {
  const [users, setUsers] = useState<UserView[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const [pending, setPending] = useState<Pending | null>(null);
  const [adding, setAdding] = useState(false);

  /**
   * A screen reader does not re-announce a live region whose text is unchanged,
   * and doing the same thing to two people in a row is ordinary here. The
   * alternating zero-width space guarantees a change without a visible one.
   */
  const announce = useCallback((text: string) => {
    setAnnouncement((previous) => (previous.endsWith('\u200B') ? text : `${text}\u200B`));
  }, []);

  const load = useCallback(async () => {
    try {
      const { users: rows } = await api.listUsers();
      setUsers(rows);
      setError(null);
    } catch (cause) {
      setError(message(cause));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Every mutation ends by reloading the list rather than patching the row it
   * changed. The routes do not promise a response body, and a suspension can
   * move more than the row it was aimed at.
   *
   * Returns whether it worked, because the caller has to know. A rejected
   * create is nearly always a password the server thought too short, and
   * closing the form on the way out would throw away the three fields the
   * operator had got right along with the one they had not.
   */
  const run = async (action: () => Promise<void>, said: string): Promise<boolean> => {
    setBusy(true);
    setError(null);
    setStatus(null);
    try {
      await action();
      await load();
      setPending(null);
      setStatus(said);
      announce(said);
      return true;
    } catch (cause) {
      const text = message(cause);
      setError(text);
      announce(`That did not happen. ${text}`);
      return false;
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="us-panel" aria-labelledby="users-heading">
      <h2 id="users-heading">People</h2>
      <p className="lead">
        Everyone who can sign in to this deployment. Accounts are created here — there is no
        self-service sign-up and no password reset by email, which is deliberate for a console that
        holds several organisations&rsquo; security posture.
      </p>

      <p aria-live="polite" role="status" className="sr-only">
        {announcement}
      </p>

      <div className="callout us-roles">
        <span className="callout-title">What the three roles can do</span>
        <ul className="us-role-list">
          <li>
            <strong>Owner</strong> manages people and deployment settings, and does everything an
            engineer does.
          </li>
          <li>
            <strong>Engineer</strong> does the daily work: connecting clients, running assessments,
            acknowledging alerts, recording attestations.
          </li>
          <li>
            <strong>Viewer</strong> reads everything and changes nothing.
          </li>
        </ul>
      </div>

      {error !== null && <div className="error us-error">{error}</div>}
      {status !== null && <p className="status-inline us-status">{status}</p>}

      <div className="toolbar us-actions">
        <button
          type="button"
          className="btn btn-primary"
          aria-expanded={adding}
          onClick={() => {
            setAdding((open) => !open);
            setPending(null);
          }}
        >
          {adding ? 'Cancel' : 'Add someone'}
        </button>
      </div>

      {adding && (
        <NewAccount
          busy={busy}
          onCancel={() => setAdding(false)}
          onCreate={async (draft) => {
            const created = await run(
              () => api.createUser(draft),
              `${draft.displayName} now has an account, as ${ROLE_TEXT[draft.role].toLowerCase()}.`,
            );
            if (created) setAdding(false);
            return created;
          }}
        />
      )}

      {loading ? (
        <p className="note">Loading…</p>
      ) : users.length === 0 ? (
        <div className="empty">
          <span className="empty-title">No accounts are listed</span>
          <p className="empty-body">
            That should not be possible while you are signed in, so it most likely means the list
            could not be read. Reload the console.
          </p>
        </div>
      ) : (
        <div className="tbl-wrap us-wrap">
          <table className="tbl us-table">
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Email</th>
                <th scope="col">Role</th>
                <th scope="col">Status</th>
                <th scope="col">Added</th>
                <th scope="col">Last signed in</th>
                <th scope="col">Manage</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <UserRows
                  key={user.id}
                  user={user}
                  me={me}
                  busy={busy}
                  pending={pending !== null && pending.userId === user.id ? pending.kind : null}
                  onOpen={(kind) => {
                    setAdding(false);
                    setPending(kind === null ? null : { userId: user.id, kind });
                  }}
                  onRun={run}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

interface NewAccountProps {
  readonly busy: boolean;
  readonly onCancel: () => void;
  readonly onCreate: (draft: {
    email: string;
    displayName: string;
    role: Role;
    password: string;
  }) => Promise<boolean>;
}

const NewAccount = ({ busy, onCancel, onCreate }: NewAccountProps) => {
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState<Role>('engineer');
  const [password, setPassword] = useState('');

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (busy) return;
    // The password is dropped whatever happens: a rejected create is worth
    // retyping one field for, and holding somebody else's password in state
    // after the request that needed it is not.
    await onCreate({ email, displayName, role, password });
    setPassword('');
  };

  return (
    <div className="card us-new">
      <div className="card-head">
        <h3>Add someone</h3>
      </div>
      <div className="card-body">
        <form onSubmit={(event) => void submit(event)} noValidate>
          <div className="field">
            <label htmlFor="new-name">Name</label>
            <input
              id="new-name"
              type="text"
              autoComplete="off"
              value={displayName}
              disabled={busy}
              onChange={(event) => setDisplayName(event.target.value)}
            />
          </div>

          <div className="field">
            <label htmlFor="new-email">Email address</label>
            <input
              id="new-email"
              type="email"
              autoComplete="off"
              autoCapitalize="off"
              spellCheck={false}
              value={email}
              disabled={busy}
              onChange={(event) => setEmail(event.target.value)}
            />
          </div>

          <div className="field">
            <label htmlFor="new-role">Role</label>
            <select
              id="new-role"
              value={role}
              disabled={busy}
              onChange={(event) => setRole(event.target.value as Role)}
            >
              {ROLES.map((option) => (
                <option key={option} value={option}>
                  {ROLE_TEXT[option]}
                </option>
              ))}
            </select>
          </div>

          <div className="field">
            <label htmlFor="new-password">First password</label>
            <input
              id="new-password"
              type="password"
              autoComplete="new-password"
              value={password}
              disabled={busy}
              aria-describedby="new-password-help"
              onChange={(event) => setPassword(event.target.value)}
            />
            <span className="hint" id="new-password-help">
              Give it to them over something other than email, and ask them to change it from their
              own Account tab. The server sets the minimum length and will say so if this is too
              short.
            </span>
          </div>

          <div className="toolbar">
            <button type="submit" className="btn btn-primary" aria-busy={busy || undefined} disabled={busy}>
              {busy ? 'Creating…' : 'Create account'}
            </button>
            <button type="button" className="btn btn-quiet" disabled={busy} onClick={onCancel}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

interface UserRowsProps {
  readonly user: UserView;
  readonly me: MeView;
  readonly busy: boolean;
  readonly pending: PendingKind | null;
  readonly onOpen: (kind: PendingKind | null) => void;
  readonly onRun: (action: () => Promise<void>, said: string) => Promise<boolean>;
}

const UserRows = ({ user, me, busy, pending, onOpen, onRun }: UserRowsProps) => {
  const self = user.id === me.id;
  const suspended = user.state === 'suspended';

  return (
    <>
      <tr className={suspended ? 'dimmed' : undefined}>
        <th scope="row" className="us-name">
          {user.displayName}
          {self && <span className="us-you"> (you)</span>}
        </th>
        <td className="mono">{user.email}</td>
        <td>{ROLE_TEXT[user.role]}</td>
        <td>
          {/* Word first, marker second: status is never carried by colour alone. */}
          <span className={suspended ? 'us-state us-state-suspended' : 'us-state'}>
            {suspended ? 'Suspended' : 'Active'}
          </span>
        </td>
        <td className="num">{formatWhen(user.createdAt)}</td>
        <td className="num">{formatWhen(user.lastSignedInAt)}</td>
        <td>
          <div className="toolbar us-row-actions">
            <Action
              label="Role"
              open={pending === 'role'}
              busy={busy}
              reason={self ? 'You cannot change your own role. Ask another owner.' : null}
              onOpen={() => onOpen(pending === 'role' ? null : 'role')}
            />
            {suspended ? (
              <Action
                label="Reinstate"
                open={pending === 'reinstate'}
                busy={busy}
                reason={null}
                onOpen={() => onOpen(pending === 'reinstate' ? null : 'reinstate')}
              />
            ) : (
              <Action
                label="Suspend"
                open={pending === 'suspend'}
                busy={busy}
                reason={self ? 'You cannot suspend your own account.' : null}
                onOpen={() => onOpen(pending === 'suspend' ? null : 'suspend')}
              />
            )}
            <Action
              label="Password"
              open={pending === 'password'}
              busy={busy}
              reason={null}
              onOpen={() => onOpen(pending === 'password' ? null : 'password')}
            />
            <Action
              label="Sign out"
              open={pending === 'sessions'}
              busy={busy}
              reason={null}
              onOpen={() => onOpen(pending === 'sessions' ? null : 'sessions')}
            />
          </div>
        </td>
      </tr>

      {pending !== null && (
        <tr className="us-drawer-row">
          <td colSpan={7}>
            <Drawer
              kind={pending}
              user={user}
              self={self}
              busy={busy}
              onCancel={() => onOpen(null)}
              onRun={onRun}
            />
          </td>
        </tr>
      )}
    </>
  );
};

interface ActionProps {
  readonly label: string;
  readonly open: boolean;
  readonly busy: boolean;
  /** Why this is unavailable, or null when it is available. Shown, not hidden. */
  readonly reason: string | null;
  readonly onOpen: () => void;
}

const Action = ({ label, open, busy, reason, onOpen }: ActionProps) => (
  <button
    type="button"
    className="btn btn-quiet btn-sm"
    aria-expanded={open}
    disabled={busy || reason !== null}
    title={reason ?? undefined}
    onClick={onOpen}
  >
    {label}
  </button>
);

interface DrawerProps {
  readonly kind: PendingKind;
  readonly user: UserView;
  readonly self: boolean;
  readonly busy: boolean;
  readonly onCancel: () => void;
  readonly onRun: (action: () => Promise<void>, said: string) => Promise<boolean>;
}

/**
 * The confirm step, in the page rather than in `window.confirm`.
 *
 * A native dialogue blocks the extension-driven browser checks this repo runs,
 * and looks nothing like the rest of the console — which matters, because the
 * point of a confirm step is that the person reads it.
 */
const Drawer = ({ kind, user, self, busy, onCancel, onRun }: DrawerProps) => {
  const [role, setRole] = useState<Role>(user.role);
  const [password, setPassword] = useState('');

  const commit = (action: () => Promise<void>, said: string) => {
    void onRun(action, said);
  };

  return (
    <div className="us-drawer">
      {kind === 'role' && (
        <>
          <p className="us-drawer-text">
            Change what <strong>{user.displayName}</strong> can do. This takes effect on their next
            request, not their next sign-in.
          </p>
          <div className="field us-drawer-field">
            <label htmlFor={`role-${user.id}`}>New role</label>
            <select
              id={`role-${user.id}`}
              value={role}
              disabled={busy}
              onChange={(event) => setRole(event.target.value as Role)}
            >
              {ROLES.map((option) => (
                <option key={option} value={option}>
                  {ROLE_TEXT[option]}
                </option>
              ))}
            </select>
          </div>
          <Confirm
            label={`Make ${user.displayName} ${ROLE_TEXT[role].toLowerCase()}`}
            busy={busy}
            disabled={role === user.role}
            onConfirm={() =>
              commit(
                () => api.setUserRole(user.id, role),
                `${user.displayName} is now ${ROLE_TEXT[role].toLowerCase()}.`,
              )
            }
            onCancel={onCancel}
          />
        </>
      )}

      {kind === 'suspend' && (
        <>
          <p className="us-drawer-text">
            Suspend <strong>{user.displayName}</strong>. They will not be able to sign in, and their
            existing sessions stop working. Nothing they have recorded is removed, and you can
            reinstate them at any time.
          </p>
          <Confirm
            label={`Suspend ${user.displayName}`}
            busy={busy}
            disabled={false}
            onConfirm={() =>
              commit(() => api.suspendUser(user.id), `${user.displayName} has been suspended.`)
            }
            onCancel={onCancel}
          />
        </>
      )}

      {kind === 'reinstate' && (
        <>
          <p className="us-drawer-text">
            Reinstate <strong>{user.displayName}</strong> as {ROLE_TEXT[user.role].toLowerCase()}.
            They will be able to sign in with the password they had before, which is worth
            replacing if you do not know why they were suspended.
          </p>
          <Confirm
            label={`Reinstate ${user.displayName}`}
            busy={busy}
            disabled={false}
            onConfirm={() =>
              commit(() => api.reinstateUser(user.id), `${user.displayName} has been reinstated.`)
            }
            onCancel={onCancel}
          />
        </>
      )}

      {kind === 'password' && (
        <>
          <p className="us-drawer-text">
            Set a new password for <strong>{user.displayName}</strong>. You cannot see their current
            one — nobody can — so this replaces it, and every session they have open ends. Tell them
            over something other than email and ask them to change it.
          </p>
          <div className="field us-drawer-field">
            <label htmlFor={`password-${user.id}`}>New password</label>
            <input
              id={`password-${user.id}`}
              type="password"
              autoComplete="new-password"
              value={password}
              disabled={busy}
              onChange={(event) => setPassword(event.target.value)}
            />
          </div>
          <Confirm
            label="Set this password"
            busy={busy}
            disabled={password.length === 0}
            onConfirm={() =>
              commit(async () => {
                await api.setUserPassword(user.id, password);
                setPassword('');
              }, `${user.displayName} has a new password, and their existing sessions were ended.`)
            }
            onCancel={onCancel}
          />
        </>
      )}

      {kind === 'sessions' && (
        <>
          <p className="us-drawer-text">
            Sign <strong>{user.displayName}</strong> out of every browser they are signed in on.
            They keep their account and can sign in again straight away.
            {self && ' This includes the session you are using now, so you will be signed out too.'}
          </p>
          <Confirm
            label="Sign them out everywhere"
            busy={busy}
            disabled={false}
            onConfirm={() =>
              commit(
                () => api.revokeUserSessions(user.id).then(() => undefined),
                `${user.displayName} has been signed out everywhere.`,
              )
            }
            onCancel={onCancel}
          />
        </>
      )}
    </div>
  );
};

interface ConfirmProps {
  readonly label: string;
  readonly busy: boolean;
  readonly disabled: boolean;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}

const Confirm = ({ label, busy, disabled, onConfirm, onCancel }: ConfirmProps) => (
  <div className="toolbar us-drawer-actions">
    <button
      type="button"
      className="btn btn-primary btn-sm"
      aria-busy={busy || undefined}
      disabled={busy || disabled}
      onClick={onConfirm}
    >
      {label}
    </button>
    <button type="button" className="btn btn-quiet btn-sm" disabled={busy} onClick={onCancel}>
      Cancel
    </button>
  </div>
);
